/**
 * Rodada crm1 (ROB-M1): teto de ids por chamada das ações em massa (Comercial, Atividades, Empresas e Contatos). Cada id
 * passa pelo caso de uso inteiro na transação da requisição, travando a linha até o COMMIT; com 500 ids a edição de uma
 * negociação esperava o lote e caía no `lock_timeout`. Regra vigente "jobs grandes em lotes de até 50 com transação curta":
 * o servidor recusa acima de 50 e a interface manda a seleção em blocos de 50, somando o resultado.
 */
export const TETO_ACAO_EM_MASSA = 50;

/** A lista em blocos de até `tamanho` (a seleção da tela em chamadas de 50). */
export function emBlocos<T>(lista: readonly T[], tamanho: number = TETO_ACAO_EM_MASSA): T[][] {
  const blocos: T[][] = [];
  for (let i = 0; i < lista.length; i += tamanho) blocos.push(lista.slice(i, i + tamanho));
  return blocos;
}

/**
 * Rodada crm2 (ROB-M1): com a seleção em blocos, a ação deixou de ser tudo ou nada. Um bloco falhou depois de outros já
 * gravados: `feitos` são os itens que passaram (a tela os tira da seleção e recarrega, para a repetição mandar só o resto).
 */
export class FalhaNoBloco<T = unknown> extends Error {
  constructor(
    readonly feitos: T[],
    readonly total: number,
    readonly causa: unknown,
  ) {
    super('falha no bloco');
    this.name = 'FalhaNoBloco';
  }
}

/** Chama em blocos, um depois do outro. Falha no primeiro bloco: o erro original (nada foi gravado); depois: `FalhaNoBloco`. */
export async function porBlocos<T, R>(lista: readonly T[], chamar: (bloco: T[]) => Promise<R>, tamanho: number = TETO_ACAO_EM_MASSA): Promise<R[]> {
  const feitos: T[] = [];
  const respostas: R[] = [];
  for (const bloco of emBlocos(lista, tamanho)) {
    try {
      respostas.push(await chamar(bloco));
    } catch (e) {
      if (!feitos.length) throw e;
      throw new FalhaNoBloco(feitos, lista.length, e);
    }
    feitos.push(...bloco);
  }
  return respostas;
}
